import { CircularProgress } from '@material-ui/core'
import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import React, { useCallback, useEffect, useState } from 'react'
import { getRecipientStatus } from 'services/api'
import { Colors } from 'styles'
import * as Styled from './Recipient.styled'
import { Container } from './Recipient.styled'
import RecipientForm from 'components/RecipientForm/RecipientForm'
import RecipientKyc from 'components/RecipientKyc/RecipientKyc'
import RecipientStatusInfo, {
  isDisplayableStatus,
} from 'components/RecipientStatusInfo/RecipientStatusInfo'
import { Config } from 'settings/config'

type RecipientStatus =
  | 'pending'
  | 'registration'
  | 'affiliation'
  | 'refused'
  | 'suspended'
  | 'active'
  | 'blocked'
  | 'inactive'

interface IError {
  message?: string
  tryAgainFunctionName?: string
}

interface RecipientProps {
  podcastId: string
}

const Recipient: React.FC<RecipientProps> = ({ podcastId }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [recipientStatus, setRecipientStatus] =
    useState<RecipientStatus | null>(null)
  const [error, setError] = useState<IError>({})
  const [kycAttempt, setKycAttempt] = useState<number | null>(null)

  const fetchRecipientStatus = useCallback(async () => {
    setIsLoading(true)
    setError({})
    const statusOrError = await getRecipientStatus(podcastId)

    if (statusOrError.hasFailed()) {
      setIsLoading(false)

      if (statusOrError.error.message === 'Recipient not found') {
        setRecipientStatus('pending')
        return
      }

      setError({
        message: 'Falha ao buscar os dados.',
        tryAgainFunctionName: 'fetchRecipientStatus',
      })
      return
    }

    setIsLoading(false)
    setRecipientStatus(statusOrError.data.status)
    setKycAttempt(statusOrError.data.kycAttempt)
  }, [podcastId])

  const fetchRecipientStatusNonBlocking = useCallback(async () => {
    const statusOrError = await getRecipientStatus(podcastId)
    if (statusOrError.hasFailed()) return
    setRecipientStatus(statusOrError.data.status)
    setKycAttempt(statusOrError.data.kycAttempt)
  }, [podcastId])

  const handleCreatedRecipient = useCallback(async () => {
    setIsLoading(true)

    let status: RecipientStatus = 'registration'
    let tries = 0
    do {
      tries++
      await new Promise((resolve) => setTimeout(resolve, 5000))
      const statusOrError = await getRecipientStatus(podcastId)

      status = statusOrError.data?.status || 'registration'
    } while (['registration', 'pending'].includes(status) && tries < 6)

    setRecipientStatus(status)
    setIsLoading(false)
  }, [podcastId])

  useEffect(() => {
    fetchRecipientStatus()
  }, [fetchRecipientStatus])

  useEffect(() => {
    if (error.message) return
    if (['pending', 'active', null].includes(recipientStatus)) return

    const interval = setInterval(() => {
      fetchRecipientStatusNonBlocking()
    }, 5000)

    return () => clearInterval(interval)
  }, [fetchRecipientStatusNonBlocking, error.message, recipientStatus])

  return (
    <Container>
      {Config.WEB_ENV === 'staging' ||
      [
        '5e7f8b4664abca0ef1f501a2',
        '614b28136cdc6538b1abbcca',
        '614b3f206cdc6538b1ac9895',
        '614b25116cdc6538b1ab7cd6',
        '672a4c25a46a47588bd22845',
        '6197f910e2dd061c4aafd6d1',
        '5e8bf5456343067441ad642b',
        '5e822a21fcc85a0edec8b567',
        '5e97711d3e17d571e59ed9da',
        '6061c7e6f37915736cd8d91d',
        '604a8c6338a4f230cb560520',
        '5eb8be70e34d735a41c3e9b9',
        '65ae96660550c6e62c4ab6de',
        '64f20e8430299007b8dfb704',
        '64e6550430299007b8bc9bcc',
        '5ea7816435d4cf0ec2bfc36f',
        '5ed14b79f3ecec6004c1d7d2',
        '603ce78538a4f230cbd37521',
        '6041e3d638a4f230cb5f6af9',
        '6594432bf8fd4f549a61db2b',
        '5e7eaab761d8770ee8c29592',
        '5e7ec4ce61d8770ee8c6de84',
        '65440661f52bf3f75624c1c0',
        '660b1a2f8d4acfab9aaa63ca',
        '64ff43af9c1ee550a55011b5',
        '5e81f9f2fcc85a0edec5a273',
        '5e87206683d35d7aae8ec045',
        '63596d0765a447074f37f367',
        '5e7fb45d61d8770ee8f00250',
        '5e7edf8f63a9860edb173e71',
        '65775b6e1c4abde3d58b4615',
      ].includes(podcastId) ? (
        isLoading ? (
          <Styled.InfoContainer>
            <CircularProgress style={{ color: Colors.PURPLE[400] }} />
            <Styled.InfoText>Carregando dados...</Styled.InfoText>
          </Styled.InfoContainer>
        ) : error.message ? (
          <Styled.InfoContainer>
            <Styled.InfoText>{error.message}</Styled.InfoText>
            <ButtonShadow
              label="Tentar novamente"
              variant="secondary"
              onPress={
                error.tryAgainFunctionName === 'fetchRecipientStatus'
                  ? fetchRecipientStatus
                  : undefined
              }
            />
          </Styled.InfoContainer>
        ) : recipientStatus === 'pending' ? (
          <RecipientForm
            podcastId={podcastId}
            handleCreatedRecipient={handleCreatedRecipient}
          />
        ) : recipientStatus === 'affiliation' ? (
          <RecipientKyc podcastId={podcastId} />
        ) : recipientStatus === 'refused' && kycAttempt > 1 ? (
          <Styled.InfoContainer>
            <Styled.InfoText>
              Aguarde algumas horas. Nosso time irá verificar o seu cadastro.
            </Styled.InfoText>
          </Styled.InfoContainer>
        ) : recipientStatus === 'registration' && kycAttempt > 1 ? (
          <Styled.InfoContainer>
            <Styled.InfoText>
              A plataforma que cuida do processo de validação dos dados está
              exigindo uma nova tentativa. Aguarde que em instantes você poderá
              tentar novamente.
            </Styled.InfoText>
          </Styled.InfoContainer>
        ) : isDisplayableStatus(recipientStatus) ? (
          <RecipientStatusInfo recipientStatus={recipientStatus} />
        ) : (
          <Styled.InfoContainer>
            <Styled.InfoText>
              Processo enviado. Você receberá os próximos passos para finalizar
              o cadastro em seu email. Você já pode sair desta página.
            </Styled.InfoText>
          </Styled.InfoContainer>
        )
      ) : (
        <Styled.InfoContainer>
          <Styled.InfoText>
            Agora, vai ser ainda mais simples receber seus pagamentos via Orelo
            🎉
            <br />
            Nos próximos dias, você poderá completar o seu registro nesta mesma
            página!
          </Styled.InfoText>
        </Styled.InfoContainer>
      )}
    </Container>
  )
}

export default Recipient
